// Thin wrapper around the map server's HTTP API. In dev, /api/maps/* is proxied
// to the Go server on :8088 (see vite.config.ts). In prod, the same proxy is
// expected from the reverse proxy. Distinct error subclasses let main.ts render
// a precise error overlay.
//
// Response types are pulled from the generated OpenAPI schema (src/gen/api.d.ts);
// changing api/openapi.yaml + re-running `npm run gen:api` is the only way to
// alter the wire format.

import type { paths, components } from './gen/api'

type GetMapResponse =
  paths['/maps/{name}']['get']['responses']['200']['content']['application/json']

export type MapDescriptor = GetMapResponse

type Schemas = components['schemas']
export type RemoteMap = Schemas['RemoteMap']
export type RemoteMapList = Schemas['RemoteMapListResponse']
export type ImportMapRequest = Schemas['ImportMapRequest']
export type ImportMapResponse = Schemas['ImportMapResponse']

export class MapNotFoundError extends Error {
  override readonly name = 'MapNotFoundError'
  constructor(public readonly mapName: string) {
    super(`Map not found: ${mapName}`)
  }
}

export class MapServerUnreachableError extends Error {
  override readonly name = 'MapServerUnreachableError'
  constructor(public readonly reason: unknown) {
    super('Map server unreachable')
  }
}

export class MapParseError extends Error {
  override readonly name = 'MapParseError'
  constructor(public readonly reason: unknown) {
    super('Failed to parse map descriptor')
  }
}

export async function fetchMap(name: string): Promise<MapDescriptor> {
  let response: Response
  try {
    response = await fetch(`/api/maps/${encodeURIComponent(name)}`, {
      headers: { Accept: 'application/json' },
    })
  } catch (err) {
    throw new MapServerUnreachableError(err)
  }

  if (response.status === 404) {
    throw new MapNotFoundError(name)
  }
  if (!response.ok) {
    throw new MapServerUnreachableError(new Error(`HTTP ${response.status}`))
  }

  try {
    return (await response.json()) as MapDescriptor
  } catch (err) {
    throw new MapParseError(err)
  }
}

// RemoteMapError carries the server's HTTP status and plain-text reason so the
// browser UI can distinguish "disabled" (503) from bad input (4xx) from an
// upstream GitHub failure (502).
export class RemoteMapError extends Error {
  override readonly name = 'RemoteMapError'
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
  }
  get disabled(): boolean {
    return this.status === 503
  }
}

export async function listMaps(): Promise<string[]> {
  let response: Response
  try {
    response = await fetch('/api/maps', { headers: { Accept: 'application/json' } })
  } catch (err) {
    throw new MapServerUnreachableError(err)
  }
  if (!response.ok) {
    throw new MapServerUnreachableError(new Error(`HTTP ${response.status}`))
  }
  const body = (await response.json()) as Schemas['MapListResponse']
  return body.maps
}

export async function browseRemote(repo: string, ref: string): Promise<RemoteMapList> {
  const params = new URLSearchParams()
  if (repo) params.set('repo', repo)
  if (ref) params.set('ref', ref)
  const query = params.toString()

  let response: Response
  try {
    response = await fetch(`/api/maps/remote${query ? `?${query}` : ''}`, {
      headers: { Accept: 'application/json' },
    })
  } catch (err) {
    throw new MapServerUnreachableError(err)
  }
  if (!response.ok) {
    throw new RemoteMapError(response.status, (await response.text()).trim() || `HTTP ${response.status}`)
  }
  return (await response.json()) as RemoteMapList
}

export async function importMap(request: ImportMapRequest): Promise<ImportMapResponse> {
  let response: Response
  try {
    response = await fetch('/api/maps/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(request),
    })
  } catch (err) {
    throw new MapServerUnreachableError(err)
  }
  if (!response.ok) {
    throw new RemoteMapError(response.status, (await response.text()).trim() || `HTTP ${response.status}`)
  }
  return (await response.json()) as ImportMapResponse
}
